import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCursorToast } from '../components/CursorToast';
import { FileText, Wand2, Sparkles, ArrowRight, CheckCircle, Crown, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function ListLandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useCursorToast();
  
  // Check if user has WANTD+ access (premium or active trial)
  const hasPremiumAccess = user?.is_premium || user?.is_trial_active;

  const handleAutoListClick = () => {
    if (!hasPremiumAccess) {
      toast.error('Auto-List from Photo(s) is a WANTD+ feature. Upgrade to access AI-powered bulk listings!');
      return;
    }
    navigate('/list/photos');
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-black text-[var(--theme-text-primary)] mb-3">
            How would you like to add items?
          </h1>
          <p className="text-[var(--theme-text-secondary)]">
            Choose the method that works best for you
          </p>
        </div>

        {/* Options Grid - Similar to old design */}
        <div className="grid sm:grid-cols-2 gap-6">
          {/* Add a Specific Item */}
          <button
            onClick={() => navigate('/list/item')}
            className="group p-8 rounded-2xl bg-[var(--theme-card)] border-2 border-[var(--theme-border)] hover:border-[#1E3A5F] transition-all text-left hover:shadow-lg"
            data-testid="list-single-item-btn"
          >
            <div className="w-16 h-16 rounded-2xl bg-[#1E3A5F]/10 flex items-center justify-center mb-4 group-hover:bg-[#1E3A5F] transition-colors">
              <FileText className="w-8 h-8 text-[#1E3A5F] group-hover:text-white transition-colors" />
            </div>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">
              Add a Specific Item
            </h2>
            <p className="text-[var(--theme-text-secondary)] mb-4">
              Manually enter details for a single item you want to list for sale.
            </p>
            <div className="flex items-center gap-2 text-[var(--theme-text-primary)] font-medium">
              <span>Add an Item</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          {/* Auto-List from Photos - Shows error toast for non-premium */}
          <button
            onClick={handleAutoListClick}
            className="group p-8 rounded-2xl bg-[var(--theme-card)] border-2 border-[var(--theme-border)] hover:border-[#C9956C] transition-all text-left hover:shadow-lg relative overflow-hidden"
            data-testid="list-bulk-photos-btn"
          >
            {/* Premium badge */}
            <div className="absolute top-4 right-4">
              <span className="px-2 py-1 rounded-full bg-[#C9956C]/10 text-[#C9956C] text-xs font-bold flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                WANTD+
              </span>
            </div>
            
            <div className="w-16 h-16 rounded-2xl bg-[#C9956C]/10 flex items-center justify-center mb-4 group-hover:bg-[#C9956C] transition-colors">
              <Wand2 className="w-8 h-8 text-[#C9956C] group-hover:text-white transition-colors" />
            </div>
            <h2 className="text-xl font-bold text-[var(--theme-text-primary)] mb-2">
              Auto-List from Photo(s)
            </h2>
            <p className="text-[var(--theme-text-secondary)] mb-2">
              Upload photos and our AI will identify items automatically. Great for multiple items!
            </p>
            <p className="text-xs text-[var(--theme-text-muted)] mb-4">
              Up to 25 items per photo scan
            </p>
            <div className="flex items-center gap-2 text-[#C9956C] font-medium">
              <span>Add items</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>

        {/* Upgrade CTA - Only show for free users */}
        {!hasPremiumAccess && (
          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-r from-[#C9956C]/10 to-[#1E3A5F]/10 border border-[#C9956C]/20">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#C9956C] flex items-center justify-center flex-shrink-0">
                <Crown className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h3 className="font-bold text-[var(--theme-text-primary)] mb-1">
                  Unlock AI-Powered Bulk Listing
                </h3>
                <p className="text-sm text-[var(--theme-text-secondary)]">
                  Upgrade to WANTD+ and list up to 25 items from a single photo with our AI scanner
                </p>
              </div>
              <Button 
                onClick={() => navigate('/premium')}
                className="bg-[#C9956C] hover:bg-[#B8845B] text-white rounded-full px-6 flex items-center gap-2"
              >
                <Zap className="w-4 h-4" />
                Upgrade Now
              </Button>
            </div>
          </div>
        )}

        {/* Benefits section */}
        <div className="mt-8 p-6 rounded-2xl bg-[#1E3A5F]/5 border border-[#1E3A5F]/10">
          <h4 className="font-bold text-[var(--theme-text-primary)] mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#C9956C]" />
            Why List on WANTD?
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#059669] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-[var(--theme-text-primary)]">Free to List</p>
                <p className="text-sm text-[var(--theme-text-secondary)]">Free to list your items</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#059669] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-[var(--theme-text-primary)]">No Commitment</p>
                <p className="text-sm text-[var(--theme-text-secondary)]">Only sell when you find the right buyer</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#059669] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-[var(--theme-text-primary)]">Smart Matching</p>
                <p className="text-sm text-[var(--theme-text-secondary)]">We find buyers looking for exactly your items</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
